// NotificationsPanel — the bell pop-up for the merchant dashboard.
// Recreated from the "Notifications · Option B (tabbed)" Claude Design handoff
// (notifications-app.jsx):
//   variant="desktop" → dropdown panel anchored under the topbar bell, over a
//                        dimmed scrim.
//   variant="mobile"  → full-screen list.
//
// Two tabs split the two notification tiers:
//   - "Needs action" — pending booking requests (inline Approve / Decline, with
//     multi-pet requests grouped by group_id). Cleared only by acting on them —
//     never by "Mark all read".
//   - "Activity" — everything else (client messages + already-acted requests).
//     "Mark all read" lives only here and clears unread messages.
// Colors/spacing track the .v-lav design theme.
import { useState } from 'react'
import { Close as CloseIcon } from '@mui/icons-material'
import { IconButton } from '@mui/material'

const FONT = "'Outfit', 'Inter', sans-serif"

// Icon chip colors per notification type (from NF_CHIP in the design).
const NF_CHIP = {
	booking: { bg: '#EDE9FB', fg: '#5F4EC2' },
	message: { bg: '#E8F2FA', fg: '#3E8BC0' },
	cancel: { bg: '#FBECEC', fg: '#C24A38' },
}

function NfIc({ name, size = 16, sw = 1.7, color = 'currentColor' }) {
	const P = {
		booking: (
			<>
				<rect x="3" y="4.5" width="18" height="16.5" rx="3" />
				<path d="M3 9.5h18M8 2.5v4M16 2.5v4M12 13v5M9.5 15.5h5" />
			</>
		),
		message: (
			<path d="M20.5 11.3a7.7 7.7 0 0 1-10.8 7L4.5 19.5l1.3-4.6A7.7 7.7 0 1 1 20.5 11.3z" />
		),
		paw: (
			<>
				<circle cx="7" cy="9.3" r="1.8" />
				<circle cx="12" cy="7.3" r="1.9" />
				<circle cx="17" cy="9.3" r="1.8" />
				<path d="M12 12.2c2.7 0 4.9 2 4.9 4.3 0 1.8-1.4 3-3.1 3-.7 0-1.3-.2-1.8-.2s-1.1.2-1.8.2c-1.7 0-3.1-1.2-3.1-3 0-2.3 2.2-4.3 4.9-4.3z" />
			</>
		),
		chevron: <path d="M9 6l6 6-6 6" />,
		check: <path d="M5 12.5l4.5 4.5L19 7" />,
	}
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke={color}
			strokeWidth={sw}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			style={{ flex: 'none' }}
		>
			{P[name] || null}
		</svg>
	)
}

// A notification "needs action" while it's a pending request — viewing or
// "Mark all read" never clears it; only Approve / Decline does.
const isPendingRequest = (n) => n.kind === 'request' && n.status === 'pending'

// Today / Yesterday / Earlier grouping for the Activity feed.
function nfGroups(list) {
	const order = [
		['today', 'Today'],
		['yesterday', 'Yesterday'],
		['earlier', 'Earlier'],
	]
	return order
		.map(([k, label]) => ({ key: k, label, items: list.filter((n) => n.bucket === k) }))
		.filter((g) => g.items.length > 0)
}

function NotifRow({ n, onApprove, onDecline, onOpenMessage }) {
	const isReq = n.kind === 'request'
	const pending = isReq && n.status === 'pending'
	// Expired request — the requested slot has already passed, so the only
	// action left is to dismiss it (which cancels, same as Decline).
	const expired = isReq && n.firstAppointment && new Date(n.firstAppointment.start_ts) < new Date()
	const chip = NF_CHIP[isReq ? n.sub : n.kind] || NF_CHIP.message
	const iconName = isReq ? 'booking' : n.kind
	const clickable = !isReq

	return (
		<div
			onClick={clickable ? () => onOpenMessage(n) : undefined}
			style={{
				position: 'relative',
				display: 'flex',
				gap: 11,
				padding: '13px 14px',
				borderRadius: 14,
				background: '#FFFFFF',
				border: '1px solid #EFEBF7',
				cursor: clickable ? 'pointer' : 'default',
				transition: 'background 0.12s',
			}}
			onMouseEnter={clickable ? (e) => (e.currentTarget.style.background = '#FBFAFE') : undefined}
			onMouseLeave={clickable ? (e) => (e.currentTarget.style.background = '#FFFFFF') : undefined}
		>
			{(pending || (!isReq && n.unread)) ? (
				<div
					style={{
						position: 'absolute',
						left: 0,
						top: 13,
						bottom: 13,
						width: 3,
						borderRadius: '0 3px 3px 0',
						background: pending ? chip.fg : '#7C6BD9',
					}}
				/>
			) : null}

			<div
				style={{
					width: 38,
					height: 38,
					flex: 'none',
					borderRadius: 11,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					background: chip.bg,
					color: chip.fg,
				}}
			>
				<NfIc name={iconName} size={19} />
			</div>

			<div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					{isReq ? (
						<span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: chip.fg, whiteSpace: 'nowrap' }}>
							{n.title}
						</span>
					) : (
						<strong style={{ fontSize: 13.5, fontWeight: 750, color: '#2E2A40', whiteSpace: 'nowrap' }}>
							{n.title}
						</strong>
					)}
					<span style={{ flex: 1, minWidth: 6 }} />
					<span style={{ fontSize: 11.5, color: '#9A93B4', fontWeight: 600, whiteSpace: 'nowrap' }}>
						{n.time}
					</span>
					{!isReq && n.unread ? (
						<div style={{ width: 7, height: 7, flex: 'none', borderRadius: '50%', background: '#7C6BD9' }} />
					) : null}
					{clickable ? <NfIc name="chevron" size={15} sw={2} color="#C3BCD6" /> : null}
				</div>

				{isReq ? (
					<div style={{ marginTop: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, rowGap: 4 }}>
						<span style={{ fontSize: 17, fontWeight: 800, color: '#2E2A40', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
							{n.client}
						</span>
					</div>
				) : (
					<span style={{ fontSize: 12, color: '#8B85A4', fontWeight: 600 }}>
						{n.pet ? `${n.client} · ${n.pet}` : n.client}
					</span>
				)}

				{n.kind === 'message' ? (
					<div
						style={{
							marginTop: 5,
							background: '#F6F4FC',
							borderRadius: 9,
							padding: '8px 11px',
							fontSize: 12.5,
							color: '#544F69',
							fontWeight: 500,
							lineHeight: 1.45,
						}}
					>
						{'“' + n.preview + '”'}
					</div>
				) : null}

				{isReq ? (
					<>
						<div style={{ marginTop: 10, background: '#F7F6FB', borderRadius: 12, padding: '11px 13px' }}>
							<div style={{ fontSize: 15, fontWeight: 800, color: '#2E2A40', letterSpacing: '-0.01em', lineHeight: 1.4 }}>
								{n.whenDate + ' · ' + n.whenTime}
							</div>
							<div style={{ marginTop: 2, fontSize: 12.5, fontWeight: 600, color: '#8B85A4', lineHeight: 1.4 }}>
								{n.groomer}
								{n.pets.length > 1 ? ' · ' + n.pets.length + ' pets' : ''}
								{n.total ? ' · ' + n.total : ''}
							</div>
							<div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #E9E4F4', display: 'flex', flexDirection: 'column' }}>
								{n.pets.map((p, i) => (
									<div key={`${p.name}-${i}`} style={{ marginTop: i > 0 ? 10 : 0, paddingTop: i > 0 ? 10 : 0, borderTop: i > 0 ? '1px dashed #E2DCF0' : 'none' }}>
										<div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
											<span style={{ width: 22, height: 22, flex: 'none', borderRadius: 7, background: '#FFFFFF', border: '1px solid #E5DFF2', color: '#7C6BD9', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
												<NfIc name="paw" size={13} sw={1.6} />
											</span>
											<span style={{ fontSize: 13, fontWeight: 750, color: '#2E2A40', whiteSpace: 'nowrap' }}>{p.name}</span>
										</div>
										<div style={{ margin: '3px 0 0 29px', fontSize: 12.5, lineHeight: 1.4 }}>
											<span style={{ fontWeight: 700, color: '#3A3650' }}>{p.service}</span>
											{n.pets.length > 1 && p.duration ? <span style={{ fontWeight: 600, color: '#8B85A4' }}>{' · ' + p.duration}</span> : null}
										</div>
										{p.addons && p.addons.length ? (
											<div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, margin: '6px 0 0 29px' }}>
												{p.addons.map((a, ai) => (
													<span key={`${a}-${ai}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: '#4A4560', background: '#FFFFFF', border: '1px solid #E7E2F1', borderRadius: 999, padding: '2px 9px 2px 7px' }}>
														<span style={{ color: '#7C6BD9', fontWeight: 800 }}>+</span>{a}
													</span>
												))}
											</div>
										) : null}
									</div>
								))}
							</div>
						</div>

						{n.status === 'pending' && expired ? (
							<div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
								<button
									type="button"
									onClick={() => onDecline(n)}
									style={{
										flex: 1,
										display: 'inline-flex',
										alignItems: 'center',
										justifyContent: 'center',
										height: 38,
										padding: '0 14px',
										border: '1.5px solid #E2DCF0',
										borderRadius: 10,
										background: '#FFFFFF',
										color: '#6E6886',
										fontFamily: 'inherit',
										fontSize: 13,
										fontWeight: 650,
										cursor: 'pointer',
										whiteSpace: 'nowrap',
									}}
								>
									Dismiss
								</button>
							</div>
						) : null}

						{n.status === 'pending' && !expired ? (
							<div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
								<button
									type="button"
									onClick={() => onApprove(n)}
									style={{
										flex: 1,
										display: 'inline-flex',
										alignItems: 'center',
										justifyContent: 'center',
										height: 38,
										padding: '0 14px',
										border: 'none',
										borderRadius: 10,
										background: '#7C6BD9',
										color: '#FFFFFF',
										fontFamily: 'inherit',
										fontSize: 13,
										fontWeight: 750,
										cursor: 'pointer',
										whiteSpace: 'nowrap',
										boxShadow: '0 2px 8px rgba(124,107,217,0.28)',
									}}
									onMouseEnter={(e) => (e.currentTarget.style.background = '#5F4EC2')}
									onMouseLeave={(e) => (e.currentTarget.style.background = '#7C6BD9')}
								>
									{n.approveLabel}
								</button>
								<button
									type="button"
									onClick={() => onDecline(n)}
									style={{
										flex: 'none',
										display: 'inline-flex',
										alignItems: 'center',
										justifyContent: 'center',
										height: 38,
										padding: '0 14px',
										border: '1.5px solid #E2DCF0',
										borderRadius: 10,
										background: '#FFFFFF',
										color: '#6E6886',
										fontFamily: 'inherit',
										fontSize: 13,
										fontWeight: 650,
										cursor: 'pointer',
										whiteSpace: 'nowrap',
									}}
								>
									Decline
								</button>
								{n.clientId ? (
									<button
										type="button"
										onClick={() => onOpenMessage(n)}
										aria-label={'Message ' + n.client}
										title={'Message ' + n.client}
										style={{ flex: 'none', width: 38, height: 38, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #E2DCF0', borderRadius: 10, background: '#FFFFFF', color: '#6E6886', cursor: 'pointer', padding: 0 }}
									>
										<NfIc name="message" size={16} sw={1.8} />
									</button>
								) : null}
							</div>
						) : null}

						{n.status === 'approved' ? (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 9,
									marginTop: 10,
									background: '#E9F6EE',
									border: '1px solid #C9E8D4',
									borderRadius: 10,
									padding: '9px 11px',
								}}
							>
								<NfIc name="check" size={16} sw={2.2} color="#2F8A5B" />
								<span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: '#25714A' }}>
									{n.confirm}
								</span>
							</div>
						) : null}

						{n.status === 'declined' ? (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 9,
									marginTop: 10,
									background: '#F4F2FA',
									border: '1px solid #E7E2F3',
									borderRadius: 10,
									padding: '9px 11px',
								}}
							>
								<span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: '#8B85A4' }}>
									{expired ? 'Request dismissed' : 'Request declined'}
								</span>
							</div>
						) : null}
					</>
				) : null}
			</div>
		</div>
	)
}

function EmptyState({ title, subtitle, size = 46 }) {
	return (
		<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9, padding: '48px 20px' }}>
			<div
				style={{
					width: size,
					height: size,
					borderRadius: '50%',
					background: '#E9F6EE',
					color: '#2F8A5B',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<NfIc name="check" size={size / 2} sw={2.2} />
			</div>
			<div style={{ fontSize: 14, fontWeight: 750, color: '#2E2A40' }}>{title}</div>
			{subtitle ? <div style={{ fontSize: 12.5, color: '#8B85A4', fontWeight: 600 }}>{subtitle}</div> : null}
		</div>
	)
}

// Segmented "Needs action" / "Activity" tabs.
function SegTabs({ tab, setTab, actionCount, activityCount, style }) {
	const items = [
		['action', 'Needs action', actionCount],
		['activity', 'Activity', activityCount],
	]
	return (
		<div style={{ display: 'flex', gap: 4, padding: 4, background: '#F1EEFA', borderRadius: 13, ...style }}>
			{items.map(([k, lbl, c]) => {
				const on = tab === k
				return (
					<button
						key={k}
						type="button"
						onClick={() => setTab(k)}
						style={{
							flex: 1,
							height: 36,
							border: 'none',
							borderRadius: 10,
							background: on ? '#FFFFFF' : 'transparent',
							boxShadow: on ? '0 1px 4px rgba(46,42,64,0.12)' : 'none',
							color: on ? '#2E2A40' : '#8B85A4',
							fontFamily: 'inherit',
							fontSize: 13,
							fontWeight: on ? 750 : 650,
							cursor: 'pointer',
							display: 'inline-flex',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 6,
						}}
					>
						{lbl}
						{c > 0 ? (
							<span
								style={{
									minWidth: 17,
									height: 17,
									padding: '0 5px',
									borderRadius: 9,
									background: k === 'action' ? '#7C6BD9' : on ? '#EDE9FB' : '#E4DFF0',
									color: k === 'action' ? '#FFFFFF' : on ? '#5F4EC2' : '#6E6886',
									fontSize: 10.5,
									fontWeight: 800,
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								{c}
							</span>
						) : null}
					</button>
				)
			})}
		</div>
	)
}

// Tab body: the action queue or the grouped activity feed.
function TabContent({ tab, pending, activity, rowProps, gap }) {
	if (tab === 'action') {
		if (pending.length === 0) {
			return <EmptyState title="All caught up" subtitle="No requests waiting on you." />
		}
		return (
			<>
				<div style={{ padding: '8px 6px 8px', fontSize: 11.5, fontWeight: 600, color: '#A79FC0' }}>
					Cleared only by approving or declining — never by “Mark all read”.
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', gap }}>
					{pending.map((n) => (
						<NotifRow key={n.id} n={n} {...rowProps} />
					))}
				</div>
			</>
		)
	}

	const groups = nfGroups(activity)
	if (groups.length === 0) {
		return <EmptyState title="No new activity" />
	}
	return groups.map((g) => (
		<div key={g.key} style={{ marginBottom: 6 }}>
			<div
				style={{
					padding: '9px 8px 5px',
					fontSize: 11,
					fontWeight: 800,
					letterSpacing: '0.06em',
					textTransform: 'uppercase',
					color: '#A39BBC',
				}}
			>
				{g.label}
			</div>
			<div style={{ display: 'flex', flexDirection: 'column', gap }}>
				{g.items.map((n) => (
					<NotifRow key={n.id} n={n} {...rowProps} />
				))}
			</div>
		</div>
	))
}

export default function NotificationsPanel({
	variant = 'desktop',
	notifications = [],
	onApprove,
	onDecline,
	onOpenMessage,
	onMarkMessagesRead,
	onClose,
}) {
	const pending = notifications.filter(isPendingRequest)
	const activity = notifications.filter((n) => !isPendingRequest(n))
	const actionCount = pending.length
	const activityUnread = activity.filter((n) => n.unread).length

	const [tab, setTab] = useState(actionCount > 0 ? 'action' : 'activity')
	const rowProps = { onApprove, onDecline, onOpenMessage }

	// ── mobile: full-screen list ───────────────────────────────────────
	if (variant === 'mobile') {
		return (
			<div
				style={{
					position: 'fixed',
					inset: 0,
					zIndex: 1300,
					background: '#FDFCFF',
					display: 'flex',
					flexDirection: 'column',
					fontFamily: FONT,
				}}
			>
				<div style={{ flex: 'none', padding: '14px 14px 10px' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
						<h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', color: '#2E2A40' }}>
							Notifications
						</h1>
						<span style={{ flex: 1 }} />
						{tab === 'activity' && activityUnread > 0 ? (
							<button
								type="button"
								onClick={onMarkMessagesRead}
								style={{
									border: 'none',
									background: '#F1EEFA',
									color: '#5F4EC2',
									fontFamily: 'inherit',
									fontSize: 12.5,
									fontWeight: 700,
									padding: '7px 12px',
									borderRadius: 99,
									cursor: 'pointer',
								}}
							>
								Mark all read
							</button>
						) : null}
						<IconButton onClick={onClose} aria-label="Close notifications" sx={{ ml: 0.5 }}>
							<CloseIcon sx={{ fontSize: 22, color: '#2E2A40' }} />
						</IconButton>
					</div>
					<SegTabs tab={tab} setTab={setTab} actionCount={actionCount} activityCount={activityUnread} style={{ marginTop: 11 }} />
				</div>
				<div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '2px 14px 24px' }}>
					<TabContent tab={tab} pending={pending} activity={activity} rowProps={rowProps} gap={7} />
				</div>
			</div>
		)
	}

	// ── desktop: scrim + dropdown panel under the bell ──────────────────
	return (
		<>
			<div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(46,42,64,0.22)' }} />
			<div
				style={{
					position: 'fixed',
					top: 70,
					right: 28,
					zIndex: 1300,
					width: 404,
					maxHeight: 'min(648px, calc(100vh - 96px))',
					background: '#FFFFFF',
					border: '1px solid #E7E2F3',
					borderRadius: 16,
					boxShadow: '0 22px 54px rgba(46,42,64,0.26)',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					fontFamily: FONT,
				}}
			>
				<div
					style={{
						position: 'absolute',
						top: -7,
						right: 18,
						width: 13,
						height: 13,
						background: '#FFFFFF',
						borderLeft: '1px solid #E7E2F3',
						borderTop: '1px solid #E7E2F3',
						transform: 'rotate(45deg)',
					}}
				/>
				<div style={{ flex: 'none', padding: '14px 16px 10px', borderBottom: '1px solid #EFEBF7' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
						<strong style={{ fontSize: 15.5, fontWeight: 800, color: '#2E2A40' }}>Notifications</strong>
						<span style={{ flex: 1 }} />
						{tab === 'activity' && activityUnread > 0 ? (
							<button
								type="button"
								onClick={onMarkMessagesRead}
								style={{
									border: 'none',
									background: 'transparent',
									color: '#7C6BD9',
									fontFamily: 'inherit',
									fontSize: 12.5,
									fontWeight: 700,
									cursor: 'pointer',
								}}
							>
								Mark all read
							</button>
						) : null}
					</div>
					<SegTabs tab={tab} setTab={setTab} actionCount={actionCount} activityCount={activityUnread} style={{ marginTop: 10 }} />
				</div>
				<div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 10px' }}>
					<TabContent tab={tab} pending={pending} activity={activity} rowProps={rowProps} gap={6} />
				</div>
			</div>
		</>
	)
}
